// ===================================================================
// src/app/pages/Login.tsx
//
// Tela de login conectada ao AuthContext.
//
// - Envia email como "username" para a API (LoginSerializer espera username)
// - Em mock mode: aceita qualquer credencial não-vazia
// - Em API mode: valida contra o backend real
// - Redireciona para "/" após login bem-sucedido
// ===================================================================

import React, { useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router';
import { nextFromSearch } from '../lib/loginRedirect';
import { useAuth } from '../contexts/AuthContext';
import { ENV } from '../lib/env';
import { ApiError } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Eye, EyeOff, Lock, Mail, Sparkles } from 'lucide-react';
import litaBg from '../../assets/img/lita.png';
import logoIcon from '../../assets/img/logo-icone.png';

function homeRouteFor(role: string | undefined, isStaff: boolean | undefined): string {
  // Contas is_staff (equipe interna Eleve.IA) recebem role='manager' por padrão no backend
  // (signal create_superuser_profile), mas não têm escola vinculada (profile.school=null) —
  // mandar pra '/inicio' (dashboard escolar) quebra a experiência. Vai direto pra área interna.
  if (isStaff) return '/interno/dashboard';
  return role === 'manager' || role === 'coordinator' ? '/inicio' : '/';
}

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  // Link aberto antes do login (?next=) — volta para ele em vez do Início.
  const next = nextFromSearch(location.search);
  const { login, user, isAuthenticated, isLoading: authLoading } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Se já está autenticado, redireciona
  if (authLoading) return null;
  if (isAuthenticated) return <Navigate to={next ?? homeRouteFor(user?.profile?.role, user?.is_staff)} replace />;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const loggedInUser = await login({
        // A API espera "username", mas o form aceita username OU email como identificador.
        username: email,
        password,
      });
      navigate(next ?? homeRouteFor(loggedInUser.profile?.role, loggedInUser.is_staff), { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        // DRF retorna { detail: [...], code: [...] } quando o serializer levanta
        // ValidationError(dict) — ambos chegam como listas.
        const codeList = err.data?.code;
        const code = Array.isArray(codeList) ? codeList[0] : codeList;
        const detailList = err.data?.detail;
        const detail = Array.isArray(detailList) ? detailList[0] : detailList;

        if (code === 'account_pending') {
          navigate('/conta-em-analise', { state: { variant: 'pending' } });
        } else if (code === 'account_rejected') {
          navigate('/conta-em-analise', { state: { variant: 'rejected', reason: detail } });
        } else if (err.status === 400) {
          setError('E-mail ou senha incorretos.');
        } else if (err.status === 429) {
          setError('Muitas tentativas. Aguarde um momento e tente novamente.');
        } else {
          setError(err.message || 'Erro ao fazer login. Tente novamente.');
        }
      } else {
        setError('Erro de conexão. Verifique sua internet.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen">
      {/* Left Panel — Branding */}
      <div className="hidden lg:flex flex-col justify-between flex-1 bg-[#030213] p-12 relative overflow-hidden">
        {/* Background image with overlay */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url(${litaBg})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        {/* Dark overlay for text readability */}
        <div className="absolute inset-0 bg-black/40" />

        {/* Subtle grid pattern */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)',
            backgroundSize: '60px 60px',
          }}
        />

        {/* Logo */}
        <div className="relative z-10 flex items-center gap-3">
          <img 
            src={logoIcon} 
            alt="Sophia Logo" 
            className="w-12 h-12 rounded-lg object-cover"
          />
          <span className="text-2xl font-bold tracking-tight text-white">Aletheia</span>
        </div>

        {/* Tagline */}
        <div className="relative z-10 max-w-md">
          <h1 className="text-4xl font-bold text-white leading-tight mb-4">
            Gestão escolar
            <br />
            <span className="text-white/60">inteligente e conectada.</span>
          </h1>
          <p className="text-white/40 text-lg leading-relaxed">
            Automatize processos, centralize informações e tome decisões baseadas em dados.
          </p>

          {/* Mock mode indicator */}
          {ENV.isMockMode && (
            <div className="mt-6 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/20">
              <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
              <span className="text-xs text-amber-400/80 font-medium">Modo demonstração</span>
            </div>
          )}
        </div>

        <div className="relative z-10 flex items-center gap-8 text-sm text-white/40">
          <span>© {new Date().getFullYear()} Aletheia</span>
          <span className="w-1 h-1 rounded-full bg-white/20" />
          <span>Todos os direitos reservados</span>
        </div>
      </div>

      {/* Right Panel — Login Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-[var(--background)]">
        <div className="w-full max-w-[400px]">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-3 mb-10">
            <div className="w-10 h-10 rounded-xl bg-[#030213] flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <span className="text-2xl font-bold tracking-tight text-[var(--foreground)]">Aletheia</span>
          </div>

          <div className="mb-8">
            <h2 className="text-2xl font-bold text-[var(--foreground)] mb-2">
              Bem-vindo de volta
            </h2>
            <p className="text-[var(--muted-foreground)]">
              Entre com suas credenciais para acessar a plataforma.
            </p>
          </div>

          {error && (
            <div className="mb-6 p-3 rounded-lg bg-[var(--destructive)]/10 border border-[var(--destructive)]/20 text-[var(--destructive)] text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-[var(--foreground)] text-sm font-medium">
                Usuário ou E-mail
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--muted-foreground)]" />
                <Input
                  id="email"
                  type="text"
                  placeholder="seu usuário ou email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 h-11"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-[var(--foreground)] text-sm font-medium">
                  Senha
                </Label>
                <button
                  type="button"
                  className="text-xs text-[var(--muted-foreground)] hover:text-[var(--foreground)] transition-colors"
                >
                  Esqueceu a senha?
                </button>
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--muted-foreground)]" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 pr-10 h-11"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--muted-foreground)] hover:text-[var(--foreground)] transition-colors"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full h-11 text-sm font-semibold"
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Entrando...
                </span>
              ) : (
                'Entrar'
              )}
            </Button>
          </form>

          <p className="mt-8 text-center text-xs text-[var(--muted-foreground)]">
            O acesso é concedido pela sua instituição.
            <br />
            Entre em contato com o administrador caso não possua credenciais.
          </p>

          <p className="text-center text-xs text-slate-400 mt-4">
            Ao continuar, você concorda com os{' '}
            <a
              href="/termos"
              target="_blank"
              rel="noopener noreferrer"
              className="text-indigo-600 hover:underline"
            >
              Termos de Uso
            </a>
            {' '}e a{' '}
            <a
              href="/privacidade"
              target="_blank"
              rel="noopener noreferrer"
              className="text-indigo-600 hover:underline"
            >
              Política de Privacidade
            </a>
            .
          </p>
        </div>
      </div>
    </div>
  );
}
